let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

export function playSound(type: 'click' | 'play' | 'scopa' | 'shuffle' | 'deal' | 'error' | 'levelUp' | 'settebello' | 'malus2' | 'dichiarazione' | 'mescola') {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    switch (type) {
      case 'click': {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(100, now + 0.08);

        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);

        osc.start(now);
        osc.stop(now + 0.08);
        break;
      }
      case 'play': {
        // Slide sound: oscillator sliding down with a bit of noise
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'triangle';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(120, now + 0.15);

        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

        osc.start(now);
        osc.stop(now + 0.15);
        break;
      }
      case 'deal': {
        // Deal card sound: a very quick high pitch slide
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.exponentialRampToValueAtTime(300, now + 0.1);

        gain.gain.setValueAtTime(0.06, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

        osc.start(now);
        osc.stop(now + 0.1);
        break;
      }
      case 'scopa': {
        // Triumphant arpeggio: C4 (261.63), E4 (329.63), G4 (392.00), C5 (523.25)
        const notes = [261.63, 329.63, 392.00, 523.25];
        notes.forEach((freq, index) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, now + index * 0.1);

          gain.gain.setValueAtTime(0, now + index * 0.1);
          gain.gain.linearRampToValueAtTime(0.1, now + index * 0.1 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.1 + 0.25);

          osc.start(now + index * 0.1);
          osc.stop(now + index * 0.1 + 0.3);
        });
        break;
      }
      case 'shuffle': {
        // A series of rapid rustling sounds
        for (let i = 0; i < 5; i++) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = 'triangle';
          osc.frequency.setValueAtTime(100 + Math.random() * 150, now + i * 0.08);

          gain.gain.setValueAtTime(0.06, now + i * 0.08);
          gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.06);

          osc.start(now + i * 0.08);
          osc.stop(now + i * 0.08 + 0.07);
        }
        break;
      }
      case 'error': {
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();
        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc1.type = 'sawtooth';
        osc2.type = 'sawtooth';
        osc1.frequency.setValueAtTime(130, now);
        osc2.frequency.setValueAtTime(135, now);

        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 0.25);
        osc2.stop(now + 0.25);
        break;
      }
      case 'levelUp': {
        // Triumphant ascending arpeggio: C4, G4, C5, E5, G5, C6
        const notes = [261.63, 392.00, 523.25, 659.25, 783.99, 1046.50];
        notes.forEach((freq, index) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, now + index * 0.08);

          gain.gain.setValueAtTime(0, now + index * 0.08);
          gain.gain.linearRampToValueAtTime(0.08, now + index * 0.08 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.08 + 0.3);

          osc.start(now + index * 0.08);
          osc.stop(now + index * 0.08 + 0.35);
        });
        break;
      }
      case 'settebello': {
        // High-pitched magical chime arpeggio: C5, E5, G5, C6, E6, G6
        const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98];
        notes.forEach((freq, index) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, now + index * 0.05);

          gain.gain.setValueAtTime(0, now + index * 0.05);
          gain.gain.linearRampToValueAtTime(0.06, now + index * 0.05 + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.05 + 0.2);

          osc.start(now + index * 0.05);
          osc.stop(now + index * 0.05 + 0.22);
        });
        break;
      }
      case 'malus2': {
        // Sad descending growl sound (wah-wah-wah effect)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(196.00, now); // G3
        osc.frequency.exponentialRampToValueAtTime(110.00, now + 0.4); // A2/A1 area

        gain.gain.setValueAtTime(0.12, now);
        gain.gain.linearRampToValueAtTime(0.08, now + 0.15);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

        osc.start(now);
        osc.stop(now + 0.4);
        break;
      }
      case 'dichiarazione': {
        // Majestic arpeggio chord: C4, E4, G4 played together, then C5
        const baseNotes = [261.63, 329.63, 392.00];
        // Play the base chord
        baseNotes.forEach(freq => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, now);
          gain.gain.setValueAtTime(0.04, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
          osc.start(now);
          osc.stop(now + 0.25);
        });
        // Play the heroic high note 150ms later
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, now + 0.15);
        gain.gain.setValueAtTime(0, now + 0.15);
        gain.gain.linearRampToValueAtTime(0.08, now + 0.17);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15 + 0.3);
        osc.start(now + 0.15);
        osc.stop(now + 0.15 + 0.35);
        break;
      }
      case 'mescola': {
        // Shuffle: rustling sounds using rapid triangle wave chimes
        for (let i = 0; i < 8; i++) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = 'triangle';
          osc.frequency.setValueAtTime(80 + Math.random() * 220, now + i * 0.05);

          gain.gain.setValueAtTime(0.04, now + i * 0.05);
          gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.04);

          osc.start(now + i * 0.05);
          osc.stop(now + i * 0.05 + 0.05);
        }
        break;
      }
    }
  } catch (err) {
    console.error('Audio play error:', err);
  }
}

export function triggerVibration(type: 'light' | 'medium' | 'success' | 'warning') {
  try {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      switch (type) {
        case 'light':
          navigator.vibrate(15);
          break;
        case 'medium':
          navigator.vibrate(30);
          break;
        case 'success':
          navigator.vibrate([40, 30, 40]);
          break;
        case 'warning':
          navigator.vibrate([100, 50, 100]);
          break;
      }
    }
  } catch (err) {
    // Silently catch security exceptions or blocked vibration requests
  }
}
